import { useQuery } from '@tanstack/react-query';

import { assetKeys } from '@/shared/lib/query/queryKeys';

import {
  fetchContainerBaseInfo,
  fetchDatabaseBaseInfo,
  fetchDatabaseSchemaBaseInfo,
  fetchGlossaryTermBaseInfo,
  fetchServiceBaseInfo,
  fetchTableBaseInfo,
  fetchTagBaseInfo,
  fetchTopicBaseInfo,
} from '@/features/asset-detail/api';

const ASSET_FETCH_HANDLERS = {
  SERVICE: fetchServiceBaseInfo,
  TABLE: fetchTableBaseInfo,
  CONTAINER: fetchContainerBaseInfo,
  TOPIC: fetchTopicBaseInfo,
  DATABASE: fetchDatabaseBaseInfo,
  DATABASE_SCHEMA: fetchDatabaseSchemaBaseInfo,
  GLOSSARY_TERM: fetchGlossaryTermBaseInfo,
  TAG: fetchTagBaseInfo,
} as const;

const ENTITY_TYPE_TO_HANDLER_KEY: Record<string, keyof typeof ASSET_FETCH_HANDLERS> = {
  table: 'TABLE',
  database: 'DATABASE',
  databaseSchema: 'DATABASE_SCHEMA',
  container: 'CONTAINER',
  topic: 'TOPIC',
  service: 'SERVICE',
  glossaryTerm: 'GLOSSARY_TERM',
  tag: 'TAG',
};

export const useAssetBaseInfo = (entityType: string, fqn: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: assetKeys.assetBaseInfo(entityType, fqn),
    queryFn: async () => {
      const handlerKey = ENTITY_TYPE_TO_HANDLER_KEY[entityType];
      const handler = ASSET_FETCH_HANDLERS[handlerKey];

      if (!handler) {
        throw new Error(`Unsupported entity type: ${entityType}`);
      }

      return handler(fqn);
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!entityType && !!fqn,
    retry: 1,
  });
};
